import React, { useEffect, useState } from "react";
import { ActivityCard, PlayButton, ProgressPill } from "../shared";

/**
 * PointReveal — "Point at Anything" demonstrative opener.
 *
 * A grid of objects (emoji); tapping one "points" at it, revealing and
 * playing its full sentence (هَذَا كِتَابٌ / هَذِهِ سَيَّارَةٌ) with the
 * leading demonstrative highlighted. Completes when everything has been
 * pointed at.
 */
function DemonstrativeSentence({ ar }) {
  // Color the leading pointing word (هَذَا / هَذِهِ).
  const space = ar.indexOf(" ");
  if (space === -1) return ar;
  return (
    <>
      <span style={{ color: "var(--tag-violet)" }}>{ar.slice(0, space)}</span>
      {ar.slice(space)}
    </>
  );
}

export default function PointReveal({ data, onComplete, audio }) {
  const [pointedIds, setPointedIds] = useState(() => new Set());

  const items = data.items;
  const allPointed = pointedIds.size >= items.length;

  useEffect(() => {
    if (allPointed) onComplete();
  }, [allPointed, onComplete]);

  const handlePoint = (item) => {
    if (pointedIds.has(item.id)) return;
    setPointedIds((prev) => new Set(prev).add(item.id));
    audio.play(item.ar);
  };

  return (
    <ActivityCard>
      <div className="flex items-center justify-end mb-4">
        <ProgressPill>{pointedIds.size}/{items.length} pointed at</ProgressPill>
      </div>

      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
        {items.map((item) => {
          const pointed = pointedIds.has(item.id);
          const emoji = (
            <span className={pointed ? "text-4xl block" : "text-5xl block"} aria-hidden="true">
              {item.emoji}
            </span>
          );
          // A revealed card must not be a (disabled) button — that would
          // swallow clicks on the nested PlayButton.
          if (!pointed) {
            return (
              <button
                key={item.id}
                type="button"
                onClick={() => handlePoint(item)}
                className="rounded-xl px-4 pt-4 pb-3 text-center transition-all hover:brightness-110"
                style={{ background: "var(--ink)", border: "1px dashed var(--panel-line)" }}
              >
                {emoji}
                <span className="text-sm block mt-2" style={{ color: "var(--parchment-dim)" }}>
                  👉 Tap to point
                </span>
              </button>
            );
          }
          return (
            <div
              key={item.id}
              className="rounded-xl px-4 pt-4 pb-3 text-center"
              style={{ background: "var(--ink)", border: "1px solid var(--gold-dim)" }}
            >
              {emoji}
              <span className="flex items-center justify-center gap-2 mt-1.5">
                <span dir="rtl" lang="ar" className="font-arabic text-2xl leading-[1.7]" style={{ color: "var(--parchment)" }}>
                  <DemonstrativeSentence ar={item.ar} />
                </span>
                {audio.audioAllowed && (
                  <PlayButton text={item.ar} playingText={audio.playingText} onPlay={audio.play} />
                )}
              </span>
              <span className="text-xs block mt-1" style={{ color: "var(--parchment-dim)" }}>{item.en}</span>
            </div>
          );
        })}
      </div>

      {allPointed && (
        <div
          className="flex items-start gap-2.5 mt-4 px-4 py-3 rounded-xl"
          style={{ background: "var(--violet-bg)", border: "1px solid var(--panel-line)" }}
        >
          <span className="text-lg leading-none" aria-hidden="true">✨</span>
          <p className="text-sm leading-relaxed m-0" style={{ color: "var(--parchment)" }}>{data.takeaway}</p>
        </div>
      )}
    </ActivityCard>
  );
}
